import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { fetchMe, logout, api } from "@/lib/auth";
import { UserPill } from "@/components/sections";

import { initialState } from "@/components/vendor-apply/constants";
import { SubmissionReceipt } from "@/components/vendor-apply/SubmissionReceipt";
import { VendorApplyForm } from "@/components/vendor-apply/VendorApplyForm";
import { devLog } from "@/lib/log";

const LOGO_URL = "/logo-transparent.png";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ---------- Page ---------- */
const VendorApply = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState(initialState);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  // Captured snapshot of what the vendor submitted, used by the
  // confirmation page so they can review/screenshot/print it.
  const [snapshot, setSnapshot] = useState(null);
  const [mediaFiles, setMediaFiles] = useState([]);
  const [mediaOrientation, setMediaOrientation] = useState(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [me, setMe] = useState(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftLastSavedAt, setDraftLastSavedAt] = useState(null);

  // Vendor onboarding is auth-gated: vendors create an account at /signin
  // and on first sign-in are redirected here. We pre-fill the email from
  // their session so the application is permanently linked to their
  // account (no chance of typos or different addresses).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const fetched = await fetchMe();
      if (cancelled) return;
      if (!fetched) {
        navigate("/signin?next=/vendor/apply", { replace: true });
        return;
      }
      if (fetched.role === "admin") {
        navigate("/admin", { replace: true });
        return;
      }
      // If this account already has a couple application, send them home —
      // they shouldn't be filling the vendor form too.
      // If they already submitted the vendor form, bypass it on re-visit.
      try {
        const { data: status } = await api.get("/auth/my-status");
        if (cancelled) return;
        if (status.has_vendor_application) {
          navigate("/", { replace: true });
          return;
        }
        if (status.role === "couple" || status.has_couple_application) {
          navigate("/couple/signup", { replace: true });
          return;
        }
      } catch (e) {
        devLog("ld_vendor_apply post-submit best-effort failed:", e?.message);
      }
      setMe(fetched);
      setForm((s) => ({ ...s, email: fetched.email || s.email }));
      // Load any previously-saved draft so the vendor picks up where they left off.
      try {
        const { data: draft } = await api.get("/vendor-applications/draft");
        if (cancelled) return;
        if (draft && Object.keys(draft).length > 0) {
          setForm((prev) => {
            const next = { ...prev };
            Object.keys(initialState).forEach((k) => {
              if (draft[k] !== undefined && draft[k] !== null) next[k] = draft[k];
            });
            // Email always comes from the session, never the draft.
            next.email = fetched.email || next.email;
            return next;
          });
          if (draft.updated_at) setDraftLastSavedAt(draft.updated_at);
        }
      } catch (e) {
        devLog("ld_vendor_apply draft load failed:", e?.message);
      }
      setAuthChecked(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  const handleSignOut = async () => {
    await logout();
    navigate("/");
  };

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: v }));

  const handleSaveDraft = async () => {
    setSavingDraft(true);
    try {
      // Persist the current form state (no validation) so the vendor can
      // resume later. Media files are NOT uploaded here — only their URLs
      // (once uploaded) are stored. Keeping raw File objects out of the
      // draft avoids double-uploads and keeps drafts lightweight.
      const draftPayload = {
        ...form,
        // Coerce booleans to safe primitives for MongoDB.
        declaration_confirmed: !!form.declaration_confirmed,
      };
      const { data } = await api.put("/vendor-applications/draft", draftPayload);
      setDraftLastSavedAt(data?.updated_at || new Date().toISOString());
      toast("Draft saved", {
        description: "You can return to this page any time to finish it.",
        duration: 2600,
      });
    } catch (err) {
      toast("Couldn't save draft", {
        description: err?.response?.data?.detail || "Please try again.",
        duration: 3000,
      });
    } finally {
      setSavingDraft(false);
    }
  };

  const validate = () => {
    const required = [
      ["full_name", "Full name"],
      ["company_name", "Company name"],
      ["email", "Email"],
      ["contact_number", "Contact number"],
      ["coverage", "Coverage"],
      ["pricing_structure", "Pricing structure"],
      ["starting_price", "Starting price"],
      ["packages_addons", "Packages / add-ons"],
      ["public_liability_insurance", "Public liability insurance"],
      ["short_intro", "Short introduction"],
      ["business_registration", "Business registration"],
      ["accuracy_confirmed", "Accuracy declaration"],
      ["responsibility_agreed", "Responsibility declaration"],
      ["public_display_consent", "Public display consent"],
      ["content_rights_confirmed", "Content rights declaration"],
      ["marketing_consent", "Marketing consent"],
      ["uk_compliance_confirmed", "UK compliance declaration"],
    ];
    for (const [k, label] of required) {
      if (!String(form[k]).trim()) {
        toast(`Please complete: ${label}`, { duration: 2800 });
        return false;
      }
    }
    if (form.service_types.length === 0) {
      toast("Please select at least one service type", { duration: 2800 });
      return false;
    }
    if (form.based_in.length === 0) {
      toast("Please select where you are based", { duration: 2800 });
      return false;
    }
    if (!/^\S+@\S+\.\S+$/.test(form.email)) {
      toast("Please enter a valid email address", { duration: 2800 });
      return false;
    }
    // Consultation slot is compulsory. If no slots are open the SlotPicker
    // itself shows the "Please contact Lux." message — either way the form
    // can't move forward until the vendor picks a date + time.
    if (!form.intro_slot_date || !form.intro_slot_time) {
      toast("Please select an introduction slot before submitting", {
        description:
          "If no slots are showing, please contact Lux to arrange a time.",
        duration: 3400,
      });
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      // 1. Upload any attached media first
      let media_urls = [];
      let media_orientation = null;
      if (mediaFiles.length > 0) {
        const fd = new FormData();
        mediaFiles.forEach((m) => fd.append("files", m.file));
        const up = await axios.post(`${API}/uploads/vendor-media`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        media_urls = (up.data?.files || []).map((f) => f.url);
        media_orientation = mediaOrientation;
      }

      // 2. Submit the application with media references
      const { data } = await axios.post(`${API}/vendor-applications`, {
        ...form,
        media_urls,
        media_orientation,
      });
      // Capture a snapshot BEFORE clearing the form so the confirmation
      // page can show what was submitted (for screenshots / printing).
      setSnapshot({
        submittedAt: new Date(),
        applicationId: data?.id || null,
        mediaCount: mediaFiles.length,
        ...form,
      });
      setSubmitted(true);
      setForm(initialState);
      setMediaFiles([]);
      setMediaOrientation(null);
      // Clean up the draft — the submission has replaced it.
      try {
        await api.delete("/vendor-applications/draft");
      } catch (e) {
        devLog("ld_vendor_apply draft cleanup failed:", e?.message);
      }
      try {
        localStorage.setItem("ld_member", "vendor");
      } catch (e) {
        devLog("ld_vendor_apply storage failed:", e?.message);
      }
      toast("Application received", {
        description: "Thank you — we'll review your details and be in touch.",
        duration: 3500,
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast("Couldn't submit. Please try again.", {
        description: err?.response?.data?.detail || "Network error.",
        duration: 3500,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted && snapshot) {
    return <SubmissionReceipt snapshot={snapshot} />;
  }

  if (!authChecked) {
    return (
      <main
        className="min-h-[100dvh] flex items-center justify-center text-[var(--ld-ink-muted)] text-sm"
        style={{ backgroundColor: "var(--ld-ivory)" }}
      >
        Loading…
      </main>
    );
  }

  return (
    <main
      data-testid="vendor-apply-page"
      className="relative min-h-[100dvh] w-full overflow-x-hidden"
      style={{ backgroundColor: "var(--ld-ivory)" }}
    >
      {/* Top bar */}
      <header className="relative z-20 flex items-center justify-between gap-6 px-6 md:px-12 pt-5 md:pt-7">
        <Link
          to="/"
          data-testid="back-login"
          className="inline-flex items-center gap-2 text-[var(--ld-ink-muted)] hover:text-[var(--ld-ink)] transition-colors"
        >
          <ArrowLeft size={14} strokeWidth={1.6} />
          <span className="font-accent text-[10px] tracking-[0.25em]">
            Back
          </span>
        </Link>
        <div className="flex items-center gap-3">
          {me && <UserPill user={me} onSignOut={handleSignOut} />}
        </div>
      </header>

      <section className="px-6 md:px-12 py-16 md:py-24">
        <div className="mx-auto max-w-3xl">
          <div className="text-center">
            <img
              src={LOGO_URL}
              alt=""
              aria-hidden
              className="mx-auto w-[100px] h-auto"
            />
            <span className="mt-6 inline-block font-accent text-[11px] tracking-[0.25em] text-[var(--ld-gold-deep)]">
              Vendor Onboarding
            </span>
            <h1
              data-testid="apply-heading"
              className="font-serif-display mt-3 text-3xl sm:text-4xl md:text-5xl leading-[1.05] text-[var(--ld-ink)] font-medium"
            >
              Apply to be listed.
            </h1>
            <p className="mt-4 text-[15px] text-[var(--ld-ink-muted)]">
              Please answer all the questions. Required fields are marked
              with{" "}
              <span className="text-[var(--ld-gold-deep)]">*</span>.
            </p>
            <div className="mx-auto mt-6 h-px w-20 bg-[var(--ld-gold)]/60" />
          </div>

          <VendorApplyForm
            form={form}
            setForm={setForm}
            set={set}
            onSubmit={handleSubmit}
            submitting={submitting}
            mediaFiles={mediaFiles}
            setMediaFiles={setMediaFiles}
            mediaOrientation={mediaOrientation}
            setMediaOrientation={setMediaOrientation}
            onSaveDraft={handleSaveDraft}
            savingDraft={savingDraft}
            draftLastSavedAt={draftLastSavedAt}
          />
        </div>
      </section>

      <footer className="px-6 md:px-12 py-8 text-center text-xs tracking-[0.2em] uppercase text-[var(--ld-ink-muted)]">
        © {new Date().getFullYear()} · Luxé Directory
      </footer>
    </main>
  );
};

export default VendorApply;
